/**
 * Cola de eventos UX y envío batch al backend de auditoría.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { buildColmenaApiUrl } from '../api-url';
import { NAV_ITEMS } from '../../features/auth/models/nav-item.model';
import { AuthService } from '../../features/auth/services/auth.service';
import {
  AUD_CATEGORIA,
  AUD_TIPO,
  type AudEventoPayload,
} from './auditoria.models';

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_THRESHOLD = 20;
/** Tope de eventos en cola mientras un lote pendiente reintenta (evita crecimiento ilimitado). */
const MAX_QUEUE_EVENTS = 500;
/** Backoff base/max para reintentos HTTP de overflow (evita saturar el MS en outage). */
const OVERFLOW_RETRY_BASE_MS = 2_000;
const OVERFLOW_RETRY_MAX_MS = 30_000;
/**
 * Presupuesto total de cuerpos keepalive in-flight (Chrome ~64 KiB).
 * Varios `fetch({ keepalive: true })` concurrentes comparten esta cuota; superarla
 * provoca pérdida silenciosa de beacons en unload.
 */
const KEEPALIVE_TOTAL_BUDGET_BYTES = 60_000;
const BATCH_PATH = '/auditoria/eventos/batch';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

/** Lote HTTP pendiente: misma clave de idempotencia hasta que el backend confirme. */
interface PendingAuditBatch {
  batchId: string;
  events: AudEventoPayload[];
}

/**
 * Cola de eventos UX y envío batch al backend de auditoría.
 * Conserva el lote pendiente (`batchId` = `Idempotency-Key`) hasta confirmación HTTP.
 */
@Injectable({ providedIn: 'root' })
export class AuditoriaService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  private readonly queue: AudEventoPayload[] = [];
  private pendingBatch: PendingAuditBatch | null = null;
  /** Chunk de overflow en vuelo/reintento: conserva batchId hasta confirmación HTTP. */
  private overflowOffloadBatch: PendingAuditBatch | null = null;
  private flushInFlight = false;
  private overflowOffloadInFlight = false;
  /** Evita programar varios offloads en un burst síncrono de track(). */
  private overflowOffloadScheduled = false;
  private overflowRetryDelayMs = OVERFLOW_RETRY_BASE_MS;
  private overflowRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private sessionId = createRandomUuid();
  /** Bytes restantes del presupuesto keepalive para el ciclo de unload actual. */
  private keepaliveBudgetRemaining = KEEPALIVE_TOTAL_BUDGET_BYTES;

  /** Registra listeners globales de navegación y clics auditables. */
  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }
    this.initialized = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects.split('?')[0];
        const mapping = this.resolvePantalla(url);
        this.track({
          tipoEvento: AUD_TIPO.NAVIGATE,
          categoria: AUD_CATEGORIA.NAVEGACION,
          pantallaCodigo: mapping.codigo,
          pantallaLabel: mapping.label,
          ruta: url,
          mensaje: `Navegación a ${url}`,
        });
      });

    document.addEventListener(
      'click',
      (mouseEvent) => {
        const target = mouseEvent.target;
        if (!(target instanceof Element)) {
          return;
        }
        const actionable = target.closest('[data-audit-action], [data-testid]');
        if (!actionable || actionable instanceof HTMLInputElement) {
          return;
        }
        const codigo =
          actionable.getAttribute('data-audit-action') ??
          actionable.getAttribute('data-testid') ??
          undefined;
        if (!codigo) {
          return;
        }
        const label =
          actionable.getAttribute('aria-label') ??
          actionable.textContent?.trim().slice(0, 120) ??
          codigo;
        this.trackClick(codigo, label);
      },
      true,
    );

    window.addEventListener('beforeunload', () => {
      this.flush(true);
    });

    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  track(event: AudEventoPayload): void {
    const isAuthEvent =
      event.tipoEvento === AUD_TIPO.LOGIN || event.tipoEvento === AUD_TIPO.LOGOUT;
    if (!isAuthEvent && !this.authService.isAuthenticated()) {
      return;
    }
    this.queue.push({
      ...event,
      fechaEvento: event.fechaEvento ?? new Date().toISOString(),
      sesionId: event.sesionId ?? this.sessionId,
      resultado: event.resultado ?? 'OK',
    });
    this.trimQueueIfNeeded();
    if (this.queue.length >= FLUSH_THRESHOLD) {
      void this.flush();
    }
  }

  trackClick(elementoCodigo: string, elementoLabel?: string, extra?: Partial<AudEventoPayload>): void {
    const route = this.currentRoute();
    const mapping = this.resolvePantalla(route);
    this.track({
      tipoEvento: AUD_TIPO.CLICK,
      categoria: extra?.categoria ?? mapping.categoria,
      pantallaCodigo: mapping.codigo,
      pantallaLabel: mapping.label,
      ruta: route,
      elementoCodigo,
      elementoLabel,
      ...extra,
    });
  }

  trackLogin(resultado: 'OK' | 'ERROR', mensaje?: string): void {
    this.track({
      tipoEvento: AUD_TIPO.LOGIN,
      categoria: AUD_CATEGORIA.AUTH,
      pantallaCodigo: 'login',
      pantallaLabel: 'Login',
      ruta: '/login',
      resultado,
      mensaje,
    });
    void this.flush();
  }

  trackLogout(): void {
    this.track({
      tipoEvento: AUD_TIPO.LOGOUT,
      categoria: AUD_CATEGORIA.AUTH,
      pantallaCodigo: 'shell',
      pantallaLabel: 'Layout',
      ruta: this.currentRoute(),
      elementoCodigo: 'shell.logout',
      elementoLabel: 'Cerrar sesión',
    });
    void this.flush(true);
    this.sessionId = createRandomUuid();
  }

  trackErrorUi(mensaje: string, extra?: Partial<AudEventoPayload>): void {
    const route = this.currentRoute();
    const mapping = this.resolvePantalla(route);
    this.track({
      tipoEvento: AUD_TIPO.ERROR_UI,
      categoria: extra?.categoria ?? mapping.categoria,
      pantallaCodigo: mapping.codigo,
      pantallaLabel: mapping.label,
      ruta: route,
      resultado: 'ERROR',
      mensaje,
      ...extra,
    });
  }

  trackUpload(
    nombreArchivo: string,
    resultado: 'OK' | 'ERROR',
    metadata?: Record<string, unknown>,
  ): void {
    this.track({
      tipoEvento: AUD_TIPO.UPLOAD,
      categoria: AUD_CATEGORIA.ARCHIVO,
      resultado,
      mensaje: `Subida: ${nombreArchivo}`,
      metadata: { nombreArchivo, ...metadata },
    });
  }

  trackDownload(
    nombreArchivo: string,
    resultado: 'OK' | 'ERROR',
    metadata?: Record<string, unknown>,
  ): void {
    this.track({
      tipoEvento: AUD_TIPO.DOWNLOAD,
      categoria: AUD_CATEGORIA.ARCHIVO,
      resultado,
      mensaje: `Descarga: ${nombreArchivo}`,
      metadata: { nombreArchivo, ...metadata },
    });
  }

  trackApiMutation(
    metodo: string,
    ruta: string,
    status: number,
    resultado: 'OK' | 'ERROR',
    mensaje?: string,
  ): void {
    const route = this.currentRoute();
    const mapping = this.resolvePantalla(route);
    this.track({
      tipoEvento: AUD_TIPO.API_MUTATION,
      categoria: mapping.categoria,
      pantallaCodigo: mapping.codigo,
      pantallaLabel: mapping.label,
      ruta: route,
      httpMetodo: metodo,
      httpRuta: ruta,
      httpStatus: status,
      resultado,
      mensaje,
    });
  }

  /**
   * Envía el lote pendiente. Si el POST falla, reintenta el **mismo** `batchId`
   * (y la misma `Idempotency-Key`) para que el backend pueda deduplicar.
   * Eventos nuevos se quedan en cola hasta que el lote pendiente confirme.
   *
   * @param useBeacon - `true` en `beforeunload`/`logout`: fire-and-forget con keepalive.
   */
  async flush(useBeacon = false): Promise<void> {
    if (!this.canPostAuditoriaBatch() && !useBeacon) {
      return;
    }
    if (useBeacon && !this.authService.isAuthenticated()) {
      return;
    }

    if (useBeacon) {
      this.flushViaBeaconUnload();
      return;
    }

    this.promoteQueueToPending();
    if (!this.pendingBatch) {
      return;
    }

    if (this.flushInFlight) {
      return;
    }
    this.flushInFlight = true;
    const batch = this.pendingBatch;
    try {
      await this.postBatch(batch);
      if (this.pendingBatch?.batchId === batch.batchId) {
        this.pendingBatch = null;
      }
    } catch {
      // Conserva pendingBatch: el siguiente flush reutiliza batchId e Idempotency-Key.
    } finally {
      this.flushInFlight = false;
    }
  }

  /**
   * Pasa la cola a un lote pendiente solo si no hay uno en curso.
   * Evita mezclar eventos nuevos en un reintento (cambiaría el cuerpo y rompería
   * la idempotencia de la clave ya enviada).
   */
  private promoteQueueToPending(): void {
    if (this.pendingBatch || this.queue.length === 0) {
      return;
    }
    const chunkSize = Math.min(FLUSH_THRESHOLD, this.queue.length);
    this.pendingBatch = {
      batchId: createRandomUuid(),
      events: this.queue.splice(0, chunkSize),
    };
  }

  /**
   * Unload/logout: best-effort keepalive para lotes idempotentes pendientes y cola efímera chunked.
   * Reinicia el presupuesto keepalive del navegador al inicio del ciclo.
   */
  private flushViaBeaconUnload(): void {
    this.keepaliveBudgetRemaining = KEEPALIVE_TOTAL_BUDGET_BYTES;
    this.promoteQueueToPending();
    if (this.flushInFlight || this.overflowOffloadInFlight) {
      // HTTP aún dueño del lote: beacon best-effort; no liberar ownership si falla.
      if (this.pendingBatch) {
        this.tryBeaconIdempotentBatch(this.pendingBatch, 'pendingBatch');
      }
      if (this.overflowOffloadBatch) {
        this.tryBeaconIdempotentBatch(this.overflowOffloadBatch, 'overflowOffloadBatch');
      }
      this.beaconAndDrainQueue();
      return;
    }
    this.flushAllPendingViaBeacon();
  }

  /**
   * Envía lotes pendientes vía beacon y libera ownership solo si el POST keepalive
   * pudo iniciarse (token + presupuesto). Sin éxito no se descarta el lote.
   */
  private flushAllPendingViaBeacon(): void {
    if (this.pendingBatch && this.tryBeaconIdempotentBatch(this.pendingBatch, 'pendingBatch')) {
      this.pendingBatch = null;
    }
    if (
      this.overflowOffloadBatch &&
      this.tryBeaconIdempotentBatch(this.overflowOffloadBatch, 'overflowOffloadBatch')
    ) {
      this.overflowOffloadBatch = null;
    }
    // Cola efímera: mismo helper chunked que beaconAndDrainQueue (evita keepalive > límite).
    this.beaconAndDrainQueue();
  }

  /**
   * Beacon de la cola efímera sin tocar `pendingBatch` (p. ej. unload con HTTP en vuelo).
   * Chunks con batchId nuevo: estos eventos no comparten clave con `pendingBatch`.
   * Solo reencola lo **no** enviado (presupuesto/token); no duplica chunks ya disparados.
   */
  private beaconAndDrainQueue(): void {
    if (this.queue.length === 0) {
      return;
    }
    const drained = this.queue.splice(0, this.queue.length);
    const unsent = this.sendEventsViaBeaconChunked(drained);
    if (unsent.length > 0) {
      this.queue.unshift(...unsent);
    }
  }

  /**
   * Acota eventos no confirmados si un lote pendiente bloquea `promoteQueueToPending`.
   * Mid-session: HTTP confirmado con reintento idempotente; unload: beacon chunked.
   *
   * El cap (`MAX_QUEUE_EVENTS`) aplica siempre al superar el tope — también mientras el
   * overflow espera backoff (`overflowOffloadScheduled`) o arranca el offload. Sin esto,
   * un outage prolongado del MS permitiría crecimiento ilimitado de la cola en memoria.
   * `enforceQueueCap` solo recorta la cola efímera (no muta lotes con Idempotency-Key fija).
   */
  private trimQueueIfNeeded(): void {
    if (this.pendingUnsentCount() <= MAX_QUEUE_EVENTS) {
      return;
    }
    if (
      this.canPostAuditoriaBatch() &&
      !this.overflowOffloadInFlight &&
      !this.overflowOffloadScheduled
    ) {
      this.scheduleOverflowRetry(0);
    }
    this.enforceQueueCap();
  }

  /** Sesión válida **y** token disponible para POST/beacon autenticado. */
  private canPostAuditoriaBatch(): boolean {
    return this.authService.isAuthenticated() && !!this.authService.getAccessToken();
  }

  /** Programa reintento de overflow con backoff (delay 0 = intento inmediato). */
  private scheduleOverflowRetry(delayMs: number): void {
    if (this.overflowOffloadScheduled || this.overflowOffloadInFlight) {
      return;
    }
    if (!this.canPostAuditoriaBatch()) {
      this.enforceQueueCap();
      return;
    }
    if (this.pendingUnsentCount() <= MAX_QUEUE_EVENTS && !this.overflowOffloadBatch) {
      return;
    }
    this.overflowOffloadScheduled = true;
    if (this.overflowRetryTimer) {
      clearTimeout(this.overflowRetryTimer);
      this.overflowRetryTimer = null;
    }
    if (delayMs <= 0) {
      this.overflowOffloadScheduled = false;
      this.prepareOverflowOffloadBatch();
      void this.offloadOverflowQueueViaHttp();
      return;
    }
    this.overflowRetryTimer = setTimeout(() => {
      this.overflowRetryTimer = null;
      this.overflowOffloadScheduled = false;
      this.prepareOverflowOffloadBatch();
      void this.offloadOverflowQueueViaHttp();
    }, delayMs);
  }

  /**
   * Reserva un chunk de overflow de forma síncrona antes del POST async.
   * Evita que `enforceQueueCap` vacíe la cola mientras el offload arranca.
   */
  private prepareOverflowOffloadBatch(): void {
    if (this.overflowOffloadBatch || this.queue.length === 0) {
      return;
    }
    if (this.pendingUnsentCount() <= MAX_QUEUE_EVENTS) {
      return;
    }
    const chunkSize = Math.min(FLUSH_THRESHOLD, this.queue.length);
    this.overflowOffloadBatch = {
      batchId: createRandomUuid(),
      events: this.queue.splice(0, chunkSize),
    };
  }

  /** Eventos no confirmados: cola + lote pendiente + chunk de overflow en vuelo. */
  private pendingUnsentCount(): number {
    return (
      this.queue.length +
      (this.pendingBatch?.events.length ?? 0) +
      (this.overflowOffloadBatch?.events.length ?? 0)
    );
  }

  /**
   * Descarga overflow con POST HTTP. Reutiliza `overflowOffloadBatch.batchId` en reintentos
   * hasta confirmación (misma semántica que `pendingBatch`).
   */
  private async offloadOverflowQueueViaHttp(): Promise<void> {
    if (
      !this.canPostAuditoriaBatch() ||
      (this.pendingUnsentCount() <= MAX_QUEUE_EVENTS && !this.overflowOffloadBatch)
    ) {
      this.overflowOffloadScheduled = false;
      if (!this.canPostAuditoriaBatch()) {
        this.enforceQueueCap();
      }
      return;
    }
    if (this.overflowOffloadInFlight) {
      this.overflowOffloadScheduled = false;
      return;
    }
    this.overflowOffloadInFlight = true;
    this.overflowOffloadScheduled = false;
    try {
      while (this.pendingUnsentCount() > MAX_QUEUE_EVENTS || this.overflowOffloadBatch) {
        if (!this.overflowOffloadBatch) {
          this.prepareOverflowOffloadBatch();
          if (!this.overflowOffloadBatch) {
            break;
          }
        }
        try {
          await this.postBatch(this.overflowOffloadBatch);
          this.overflowOffloadBatch = null;
          this.overflowRetryDelayMs = OVERFLOW_RETRY_BASE_MS;
        } catch {
          break;
        }
      }
    } finally {
      this.overflowOffloadInFlight = false;
      if (this.pendingUnsentCount() <= MAX_QUEUE_EVENTS && !this.overflowOffloadBatch) {
        this.overflowRetryDelayMs = OVERFLOW_RETRY_BASE_MS;
        return;
      }
      if (this.canPostAuditoriaBatch() && !this.overflowOffloadScheduled) {
        const retryDelayMs = this.overflowRetryDelayMs;
        this.overflowRetryDelayMs = Math.min(
          this.overflowRetryDelayMs * 2,
          OVERFLOW_RETRY_MAX_MS,
        );
        this.scheduleOverflowRetry(retryDelayMs);
      }
      // Cap inmediato tras fallo/backoff: no esperar al próximo tick del timer.
      this.enforceQueueCap();
    }
  }

  /**
   * Recorta excedente de la cola efímera mientras hay offload en vuelo.
   * No muta `pendingBatch` ni `overflowOffloadBatch`: comparten Idempotency-Key fija.
   */
  private enforceQueueCap(): void {
    let excess = this.pendingUnsentCount() - MAX_QUEUE_EVENTS;
    if (excess <= 0) {
      return;
    }
    const dropFromQueue = Math.min(excess, this.queue.length);
    if (dropFromQueue > 0) {
      console.warn(
        `[AuditoriaService] Cola de auditoría saturada (${this.pendingUnsentCount()} eventos en espera): ` +
          `descartando ${dropFromQueue} evento(s) antiguos de la cola. Revise conectividad con el MS de auditoría.`,
      );
      this.queue.splice(0, dropFromQueue);
      excess = this.pendingUnsentCount() - MAX_QUEUE_EVENTS;
    }
    if (excess > 0) {
      console.warn(
        `[AuditoriaService] Cola de auditoría saturada (${this.pendingUnsentCount()} eventos): ` +
          `${excess} evento(s) permanecen en lotes HTTP pendientes; no se recortan para preservar idempotencia.`,
      );
    }
  }

  /**
   * Envía cola efímera vía keepalive en chunks; cada chunk usa batchId nuevo.
   * Respeta `keepaliveBudgetRemaining` (cuota total del navegador).
   *
   * @returns Eventos que no pudieron enviarse (sin reencolar lo ya disparado).
   */
  private sendEventsViaBeaconChunked(events: AudEventoPayload[]): AudEventoPayload[] {
    if (events.length === 0) {
      return [];
    }
    if (!this.authService.getAccessToken()) {
      return events;
    }

    let offset = 0;
    while (offset < events.length) {
      let size = Math.min(FLUSH_THRESHOLD, events.length - offset);
      let sent = false;
      while (size >= 1) {
        const chunk = events.slice(offset, offset + size);
        const batch: PendingAuditBatch = {
          batchId: createRandomUuid(),
          events: chunk,
        };
        if (this.sendPendingViaBeacon(batch)) {
          offset += size;
          sent = true;
          break;
        }
        if (size === 1) {
          console.warn(
            '[AuditoriaService] Presupuesto keepalive agotado o cuerpo demasiado grande: ' +
              `quedan ${events.length - offset} evento(s) sin beacon en unload.`,
          );
          return events.slice(offset);
        }
        size = Math.floor(size / 2);
      }
      if (!sent) {
        return events.slice(offset);
      }
    }
    return [];
  }

  private postBatch(batch: PendingAuditBatch): Promise<ApiEnvelope<{ ingested: number }>> {
    return firstValueFrom(
      this.http.post<ApiEnvelope<{ ingested: number }>>(
        buildColmenaApiUrl(BATCH_PATH),
        { batchId: batch.batchId, events: batch.events },
        {
          headers: {
            'Idempotency-Key': batch.batchId,
          },
        },
      ),
    );
  }

  /**
   * Beacon de lote con Idempotency-Key fija.
   * Advierte si no pudo iniciarse (sin token o presupuesto keepalive) para no ocultar
   * pérdida de auditoría en unload — mismo patrón en ruta in-flight y flushAllPending.
   */
  private tryBeaconIdempotentBatch(
    batch: PendingAuditBatch,
    label: 'pendingBatch' | 'overflowOffloadBatch',
  ): boolean {
    if (this.sendPendingViaBeacon(batch)) {
      return true;
    }
    console.warn(
      `[AuditoriaService] Beacon de ${label} no iniciado en unload ` +
        `(token ausente o presupuesto keepalive). ` +
        `${batch.events.length} evento(s) con batchId=${batch.batchId} pueden perderse.`,
    );
    return false;
  }

  /**
   * POST keepalive del lote. La cabecera replica `batchId` para alinear con el
   * contrato de idempotencia del MS de auditoría.
   * Descuenta el cuerpo del presupuesto keepalive del ciclo de unload.
   *
   * @returns `true` si el envío keepalive pudo iniciarse (hay token y cabe en presupuesto).
   */
  private sendPendingViaBeacon(batch: PendingAuditBatch): boolean {
    const token = this.authService.getAccessToken();
    if (!token) {
      return false;
    }
    const body = JSON.stringify({ batchId: batch.batchId, events: batch.events });
    const bodyBytes = new TextEncoder().encode(body).byteLength;
    if (bodyBytes > this.keepaliveBudgetRemaining) {
      return false;
    }
    this.keepaliveBudgetRemaining -= bodyBytes;
    void fetch(buildColmenaApiUrl(BATCH_PATH), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Idempotency-Key': batch.batchId,
      },
      body,
      keepalive: true,
    });
    return true;
  }

  private currentRoute(): string {
    return this.router.url.split('?')[0].replace(/\/$/, '') || '/';
  }

  private resolvePantalla(route: string): {
    codigo: string;
    label: string;
    categoria: string;
  } {
    if (route === '/login') {
      return { codigo: 'login', label: 'Login', categoria: AUD_CATEGORIA.AUTH };
    }
    for (const item of NAV_ITEMS) {
      if (route === item.route || route.startsWith(`${item.route}/`)) {
        return {
          codigo: item.codigo,
          label: item.label,
          categoria: this.categoriaFromSection(item.section),
        };
      }
    }
    if (route.startsWith('/reportes/')) {
      return {
        codigo: 'menu.reportes-trazabilidad',
        label: 'Logs del sistema',
        categoria: AUD_CATEGORIA.REPORTE,
      };
    }
    return { codigo: route, label: route, categoria: AUD_CATEGORIA.NAVEGACION };
  }

  private categoriaFromSection(section?: string): string {
    switch (section) {
      case 'Motor reglas':
        return AUD_CATEGORIA.MOTOR_REGLAS;
      case 'Mantenedores':
        return AUD_CATEGORIA.CONFIGURACION;
      case 'Reportes':
        return AUD_CATEGORIA.REPORTE;
      case 'Flujo':
      default:
        return AUD_CATEGORIA.NAVEGACION;
    }
  }
}

/** UUID v4 cuando `crypto.randomUUID` no está (p. ej. HTTP sin contexto seguro). */
function createRandomUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}
